import { Injectable } from '@angular/core';
import { AbstractControl, UntypedFormGroup } from '@angular/forms';
import { FormsValidators } from '@msslib/components';
import { FormFieldType, WrapperType, yesNoOptions } from '@msslib/components/formly/formly.config';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { IApplicants, IMortgageRequirements } from 'apps/clubhub/src/app/ignite/models/products';
import {
  MortgageLenders,
  MortgageType,
  ProductLengthExtended,
  ProductType,
  PropertyType,
  PurchaserType,
  RepaymentMethod,
  RepaymentVehicle,
} from 'apps/shared/src/models';
import {
  errorMessages,
  initialProductLengthTooltip,
} from 'apps/clubhub/src/app/ignite/models/affordability';
import { ProductsSearchService } from '../products-search.service';
import { IgniteHelperService, LtvService } from '@msslib/services';
import { LenderService } from 'apps/shared/src/services/lender.service';
import { map } from 'rxjs';
import { productFiltersNames } from 'apps/clubhub/src/app/constants';

const secondChargeLoanAmountLabel = 'Second Charge Loan Amount';
const loanAmountLabel = 'Loan Amount';
const defaultNumberOfApplicants = 1;

@Injectable({
  providedIn: 'root',
})
export class ProductsFormFieldsResiService {
  public constructor(
    private helperService: IgniteHelperService,
    private productsSearchService: ProductsSearchService,
    private lenderService: LenderService,
    private ltvService: LtvService,
  ) { }

  public resetFieldIfEmpty(field: FormlyFieldConfig) {
    if (!field.model) {
      field.formControl?.reset();
    }
  }

  public keyboardEventMarkTouched(field: FormlyFieldConfig, event: KeyboardEvent) {
    const key = event.key;
    if (!['Tab', 'Shift'].includes(key)) {
      field.formControl?.markAsTouched();
    }
  }

  public fields: FormlyFieldConfig[] = [
    {
      key: 'mortgageRequirements',
      wrappers: [WrapperType.FormCard],
      props: {
        title: 'Mortgage Details',
      },
      validators: {
        max500PercentLoanValue: {
          expression: FormsValidators.expression(FormsValidators.ltv(500)),
          message: 'Loan amount should not exceed 500% of property value',
          errorPath: 'loanAmount',
        },
        max500PercentPurchasePriceShare: {
          expression: FormsValidators.expression(FormsValidators.lts(500)),
          message: 'Purchase Price Share should not exceed 500% of Loan amount',
          errorPath: 'purchasePriceShare',
        },
        interestOnlyAmountValidator: {
          expression: (control: AbstractControl) => {
            const repaymentMethodValue = (control as any).controls?.repaymentMethod?.value?.value;
            if (repaymentMethodValue === RepaymentMethod.InterestOnlyPartAndPart) {
              const validator = FormsValidators.expression(FormsValidators
                .greaterThanField<IMortgageRequirements>('interestOnlyAmount', 'loanAmount', true));
              return validator(control);
            }

            return true;
          },
          message: errorMessages.interestOnlyLessThanLoanAmount,
          errorPath: 'interestOnlyAmount',
        },
        productTransferInitialDate: this.helperService.getProductTransferInitialPeriodValidation(),
      },
      fieldGroup: [
        {
          key: 'mortgageType',
          type: 'radio-button',
          props: {
            label: 'Mortgage Type',
            options: this.getMortgageTypeOptions(),
            required: true,
            change: (field: FormlyFieldConfig) => {
              const { mortgageType } = (field?.model ?? {}) as IMortgageRequirements;
              if (mortgageType === MortgageType.Purchase) {
                this.productsSearchService.model.mortgageRequirements.helpToBuy = false;
                this.productsSearchService.updateModel(this.productsSearchService.model);
              }
              const helpToBuyValue = mortgageType === MortgageType.Remortgage || mortgageType === MortgageType.Purchase
                ? false
                : null;
              (field.parent?.formControl as UntypedFormGroup).controls?.helpToBuy?.setValue(helpToBuyValue);

              (field.parent?.formControl as UntypedFormGroup).controls.productTypeExtended
                ?.setValue(ProductType.Standard);
            },
          },
        },
        {
          key: 'productTypeExtended',
          type: 'select',
          props: {
            label: 'Product Type',
            options: [
              { label: 'Standard', value: ProductType.Standard },
              { label: 'Shared Ownership', value: ProductType.SharedOwnership },
              { label: 'Right To Buy', value: ProductType.RightToBuy },
              { label: 'Shared Equity', value: ProductType.SharedEquity },
              { label: 'Self-Build', value: ProductType.SelfBuild },
              { label: 'Retirement Interest Only (RIO)', value: ProductType.RetirementInterestOnly },
              { label: 'Joint Borrower-Sole Proprietor', value: ProductType.JointBorrowerSoleProprietor },
            ],
            change: (field: FormlyFieldConfig, event) => {
              const isRio = this.helperService
                .getEnumValue(event.target.value) as ProductType === ProductType.RetirementInterestOnly;
              if (isRio) {
                (field.parent?.formControl as UntypedFormGroup).controls.repaymentMethod
                  .setValue({ value: RepaymentMethod.InterestOnly });
                (field.parent?.formControl as UntypedFormGroup).controls.helpToBuy
                  ?.setValue(false);
              }
            },
          },
        },
        {
          key: 'capitalRaising',
          wrappers: [WrapperType.NoWrappers],
          expressions: {
            hide: (field: FormlyFieldConfig) =>
              !this.helperService.isRemortgageRightToBuy(field.parent?.model?.mortgageType,
                field.parent?.model?.productTypeExtended),
          },
          fieldGroup: [
            {
              key: 'value',
              type: FormFieldType.RadioButtons,
              defaultValue: false,
              props: {
                label: 'Capital raising',
                tooltip: 'Additional borrowing for Right to Buy is generally only acceptable for home improvements,' +
                ' please check with the lender before proceeding',
                options: yesNoOptions,
                required: true,
              },
            },
          ],
        },
        {
          key: 'amountTransferredFromOtherLender',
          type: FormFieldType.Currency,
          props: {
            label: 'Current mortgage balance outstanding',
            tooltip: 'What is the outstanding mortgage balance from the previous lender?',
            required: true,
          },
          expressions: {
            hide: field => field.model?.mortgageType !== MortgageType.Remortgage
              || !field.model?.capitalRaising?.value,
          },
          validators: {
            validation: [FormsValidators.greaterThan(0)],
          },
        },
        {
          key: 'purchaserType',
          type: 'radio-button',
          props: {
            label: 'Purchaser type',
            options: [
              { label: 'First time buyer', value: PurchaserType.FirstTimeBuyer },
              { label: 'Homemover', value: PurchaserType.HomeMover },
            ],
          },
          expressions: {
            hide: field => field.parent?.model?.mortgageType !== MortgageType.Purchase,
          },
        },
        {
          key: 'newBuild',
          type: FormFieldType.RadioButtons,
          resetOnHide: true,
          defaultValue: null,
          props: {
            label: 'New build',
            options: yesNoOptions,
          },
          expressions: {
            hide: (field) => this.isRightToBuySelected(field),
          },
        },
        ...this.mortgageLenderFields(),
        {
          key: 'propertyType',
          type: FormFieldType.RadioButtons,
          defaultValue: PropertyType.House,
          resetOnHide: true,
          props: {
            label: 'Property type',
            options: [
              { label: 'House', value: PropertyType.House },
              { label: 'Flat', value: PropertyType.Flat },
            ],
            required: true,
          },
          expressions: {
            hide: field => !field.model?.newBuild &&
            !this.helperService.isSharedOwnership(field.model?.productTypeExtended) &&
            !((field.parent?.model?.productTypeExtended as ProductType) === ProductType.RightToBuy),
          },
        },
        {
          key: 'propertyValue',
          type: 'currency',
          props: {
            label: 'Property Value',
            required: true,
            blur: this.resetFieldIfEmpty,
          },
          validators: {
            validation: [FormsValidators.greaterThan(0)],
          },
          expressions: {
            'props.readonly': () => this.productsSearchService.propertyValueDisabled,
          },
        },
        {
          key: 'purchasePriceShare',
          type: 'currency',
          resetOnHide: true,
          props: {
            label: 'Purchase Price of Share',
            required: true,
            keyup: this.keyboardEventMarkTouched,
            blur: this.resetFieldIfEmpty,
          },
          validators: {
            validation: [FormsValidators.greaterThanOnTouched(0)],
          },
          expressions: {
            hide: field => !this.helperService.isSharedOwnership(field.parent?.model?.productTypeExtended),
          },
          hooks: {
            onInit: (field) => {
              if (field.formControl?.value === 0) {
                // Override default "Currency" control value to prevent usage of 0 value
                field.formControl.setValue(undefined);
              }
            },
          },
        },
        {
          key: 'discountedPurchasePrice',
          type: 'currency',
          resetOnHide: true,
          props: {
            label: 'Discounted Purchase Price',
            required: true,
            keyup: this.keyboardEventMarkTouched,
            blur: this.resetFieldIfEmpty,
          },
          validators: {
            validation: [FormsValidators.greaterThanOnTouched(0)],
          },
          expressions: {
            hide: field => !this.helperService.isPurchaseRightToBuy(field.parent?.model?.mortgageType,
              field.parent?.model?.productTypeExtended),
          },
          hooks: {
            onInit: (field) => {
              if (field.formControl?.value === 0) {
                // Override default "Currency" control value to prevent usage of 0 value
                field.formControl.setValue(undefined);
              }
            },
          },
        },
        {
          key: 'loanAmount',
          type: 'currency',
          props: {
            required: true,
            blur: this.resetFieldIfEmpty,
          },
          validators: {
            validation: [FormsValidators.greaterThan(0)],
          },
          expressions: {
            'props.readonly': () => this.productsSearchService.loanAmountDisabled,
            'props.label': field => {
              return field.model?.mortgageType === MortgageType.SecondCharge
                ? secondChargeLoanAmountLabel
                : loanAmountLabel;
            },
            'props.tooltip': field => {
              return this.helperService.isPurchaseRightToBuy(field.parent?.model?.mortgageType,
                field.parent?.model?.productTypeExtended) ?
                'If your client is borrowing more than the discounted purchase price to help with' +
                ' home improvements please include here' :
                null;
            },
          },
        },
        {
          key: 'additionalBorrowing',
          type: FormFieldType.RadioButtons,
          defaultValue: false,
          props: {
            label: 'Are additional funds required for home improvements?',
            options: yesNoOptions,
          },
          expressions: {
            hide: field => !this.helperService.isPurchaseRightToBuy(field.parent?.model?.mortgageType,
              field.parent?.model?.productTypeExtended),
          },
        },
        {
          key: 'outstandingBalanceCurrentMortgage',
          type: 'currency',
          props: {
            label: 'Outstanding Balance on the Current Mortgage',
            keyup: this.keyboardEventMarkTouched,
            blur: this.resetFieldIfEmpty,
          },
          validators: {
            validation: [FormsValidators.greaterThanOnTouched(0)],
          },
          expressions: {
            hide: field => field.model?.mortgageType !== MortgageType.SecondCharge,
            'props.readonly': () => this.productsSearchService.loanAmountDisabled,
          },
        },
        {
          key: 'ltv',
          type: 'disabled',
          props: {
            label: 'LTV',
            addonEnd: '%',
          },
          expressions: {
            'props.value': field =>
              this.helperService.isSharedOwnership(field.parent?.model?.productTypeExtended)
                ? this.ltvService.calculateSharePercent((field.parent?.model ?? {}) as IMortgageRequirements)
                : this.ltvService.calculateLtv((field.parent?.model ?? {}) as IMortgageRequirements),
            'props.label': field =>
              this.ltvService.getPurchaseLTVFieldLabel(field.parent?.model?.productTypeExtended),
          },
        },
        {
          key: 'lts',
          type: 'disabled',
          props: {
            label: ' LTV (Purchase Share) ',
            addonEnd: '%',
          },
          expressions: {
            hide: field => !this.helperService.isSharedOwnership(field.parent?.model?.productTypeExtended),
            'props.value': field =>
              this.ltvService.calculateLts((field.parent?.model ?? {}) as IMortgageRequirements),
          },
        },
        {
          key: 'ltvDiscountedPrice',
          type: 'disabled',
          props: {
            label: ' LTV (Discounted price) ',
            addonEnd: '%',
          },
          expressions: {
            hide: field => !this.helperService.isPurchaseRightToBuy(field.parent?.model?.mortgageType,
              field.parent?.model?.productTypeExtended),
            'props.value': field =>
              this.ltvService.calculateLtvDiscountedPrice((field.parent?.model ?? {}) as IMortgageRequirements),
          },
        },
        {
          key: 'helpToBuy',
          type: 'radio-button',
          resetOnHide: true,
          defaultValue: null,
          props: {
            label: 'Help to buy',
            tooltip: 'Only select if your customer is an existing Help to Buy scheme user',
            options: yesNoOptions,
            change: (field) => (field.form as AbstractControl).patchValue({
              repaymentMethod: { value: field.model.helpToBuy ? RepaymentMethod.CapitalAndInterest : null },
            }),
          },
          expressions: {
            hide: (field) => this.isHelpToBuyHidden(field),
            'props.disabled': (field) => field.model.productTypeExtended === ProductType.RetirementInterestOnly,
          },
        },
        {
          key: 'repaymentMethod',
          props: {
            label: 'Method of repayment',
          },
          fieldGroup: [
            {
              key: 'value',
              type: 'select',
              props: {
                allowNull: true,
                options: [
                  { label: 'Capital and interest', value: RepaymentMethod.CapitalAndInterest },
                  { label: 'Interest only', value: RepaymentMethod.InterestOnly },
                  { label: 'Interest only part and part', value: RepaymentMethod.InterestOnlyPartAndPart },
                ],
                collapseMargin: true,
              },
              expressions: {
                'props.readonly': field => this.isHelpToBuySelected(field.parent) ||
                  (field.parent?.parent?.formControl as UntypedFormGroup).controls?.productTypeExtended
                    ?.value === ProductType.RetirementInterestOnly,
              },
            },
          ],
        },
        {
          key: 'repaymentVehicle',
          type: FormFieldType.Select,
          defaultValue: null,
          props: {
            label: 'Repayment vehicle',
            tooltip: 'How will the interest only element be repaid at the end of the term?',
            options: [
              this.helperService.defaultSelectOption,
              {
                value: RepaymentVehicle.SaleOfProperty,
                label: 'Sale of property',
              },
              {
                value: RepaymentVehicle.SaleOfSecondProperty,
                label: 'Sale of second property',
              },
              {
                value: RepaymentVehicle.EndowmentsInvestmentsPension,
                label: 'Endowments / Investments / Pension',
              },
              {
                value: RepaymentVehicle.SavingsISA,
                label: 'Savings / ISA\'s',
              },
            ],
            change: (field: FormlyFieldConfig) => {
              (field.parent?.formControl as UntypedFormGroup).controls.equityValue?.setValue(
                field.parent?.model?.propertyValue - field.parent?.model?.loanAmount,
              );
            },
          },
          expressions: {
            'props.disabled': () => false,
            hide: (field) =>
              (field.parent?.model as IMortgageRequirements)?.repaymentMethod?.value !== RepaymentMethod.InterestOnly &&
              (field.parent?.model as IMortgageRequirements)
                ?.repaymentMethod?.value !== RepaymentMethod.InterestOnlyPartAndPart,
          },
        },
        {
          key: 'equityValue',
          type: FormFieldType.Currency,
          props: {
            label: 'How much equity is to remain in the property?',
            required: true,
          },
          expressions: {
            hide: field => field.model?.repaymentVehicle !== RepaymentVehicle.SaleOfProperty
              || field.model?.repaymentMethod.value === RepaymentMethod.CapitalAndInterest,
          },
          validators: {
            validation: [FormsValidators.integer, FormsValidators.greaterThan(0)],
          },
        },
        {
          key: 'interestOnlyAmount',
          type: 'currency',
          props: {
            label: 'Amount on interest only',
            required: true,
            keyup: this.keyboardEventMarkTouched,
            blur: this.resetFieldIfEmpty,
          },
          expressions: {
            hide: field => (field.parent?.model as IMortgageRequirements)?.repaymentMethod?.value
              !== RepaymentMethod.InterestOnlyPartAndPart,
          },
          validators: {
            validation: [FormsValidators.greaterThanOnTouched(0)],
          },
        },
        {
          key: 'mortgageTerm',
          fieldGroupClassName: 'row',
          wrappers: ['horizontal-field'],
          props: {
            label: 'Mortgage term',
            suppressErrors: true,
          },
          fieldGroup: [
            {
              key: 'years',
              type: 'number',
              className: 'col',
              wrappers: [WrapperType.NoWrappers],
              props: {
                min: 0,
                max: 40,
                addonEnd: 'years',
                addonEndClass: 'fw-light',
                groupedField: true,
                collapseMargin: true,
              },
              validation: [FormsValidators.integer],
            },
            {
              key: 'months',
              type: 'number',
              className: 'col',
              wrappers: [WrapperType.NoWrappers],
              props: {
                min: 0,
                max: 11,
                addonEnd: 'months',
                addonEndClass: 'fw-light',
                groupedField: true,
                collapseMargin: true,
              },
              validators: {
                validation: [FormsValidators.integer],
              },
            },
          ],
          validators: {
            max40Years: {
              expression: (control: AbstractControl) => {
                const { years, months } = control.value ?? {};
                return 12 * (years ?? 0) + (months ?? 0) <= 40 * 12;
              },
              message: 'Mortgage term has a maximum of 40 years',
            },
          },
        },
        {
          key: 'productLengths',
          type: FormFieldType.MultiSelect,
          defaultValue: [],
          props: {
            label: productFiltersNames.initialProductPeriod,
            tooltip: initialProductLengthTooltip,
            options: this.helperService.productLengthExtendedOptions(),
            required: false,
          },
        },
        {
          key: 'customProductLength',
          fieldGroupClassName: 'row',
          wrappers: ['horizontal-field'],
          props: {
            suppressErrors: true,

          },
          expressions: {
            hide: field => !(field.parent?.model as IMortgageRequirements)?.productLengths?.includes(
              ProductLengthExtended.Custom),
          },
          fieldGroup: [
            {
              key: 'from',
              type: 'number',
              className: 'col',
              name:'From',
              wrappers: [WrapperType.HorizontalField],
              resetOnHide: true,
              props: {
                min: 0,
                label: 'From',
                addonEnd: 'months',
                addonEndClass: 'fw-light',
                groupedField: true,
                collapseMargin: true,
                required: true,
              },
              validation: [FormsValidators.integer],
            },
            {
              key: 'to',
              type: 'number',
              className: 'col',
              resetOnHide: true,
              wrappers: [WrapperType.HorizontalField],
              props: {
                min: 0,
                label: 'To',
                addonEnd: 'months',
                addonEndClass: 'fw-light',
                groupedField: true,
                collapseMargin: true,
                required: true,
              },
              validators: {
                validation: [FormsValidators.integer],
              },
            },
          ],
        },
        {
          key: 'rateTypes',
          type: FormFieldType.MultiSelect,
          defaultValue: [],
          props: {
            label: 'Rate Type',
            options: this.helperService.rateTypesOptions,
            required: false,
          },
        },
        {
          key: 'applicants',
          wrappers: [WrapperType.EmptyWrapper],
          fieldGroup: [
            {
              key: 'numberOfApplicants',
              type: FormFieldType.RadioButtons,
              defaultValue: defaultNumberOfApplicants,
              props: {
                label: 'Number of applicants',
                options: [
                  { value: 1, label: '1' },
                  { value: 2, label: '2' },
                  { value: 3, label: '3' },
                  { value: 4, label: '4' },
                ],
                required: false,
                change: (field: FormlyFieldConfig) => {
                  const numberOfApplicants = +field.formControl?.value;
                  this.onNumberOfApplicantsChange(numberOfApplicants);
                },
              },
              hooks: {
                onInit: (field) => {
                  field.formControl?.setValue(defaultNumberOfApplicants);
                  this.onNumberOfApplicantsChange(defaultNumberOfApplicants);
                },
              },
            },
            {
              key: 'applicantAges',
              type: FormFieldType.Repeating,
              props: {
                suppressErrors: true,
                id: 'applicantAges',
              },
              fieldArray: {
                wrappers: [WrapperType.NoWrappers],
                props: {
                  suppressErrors: true,
                },
                fieldGroup: [
                  {
                    expressions: {
                      template: field => {
                        return `
                          <hr class="my-0" />
                          <h2 class="fw-light mt-4 mb-3">Applicant ${field.model.applicantNumber}</h2>
                        `;
                      },
                    },
                  },
                  {
                    key: 'dateOfBirth',
                    type: FormFieldType.Date,
                    props: {
                      label: 'Date of birth',
                    },
                    validators: {
                      validation: [FormsValidators.dateOrEmpty, FormsValidators.atLeast18YearsOrEmpty],
                    },
                  },
                ],
              },
            },
          ],
        },
        {
          key: 'getResultsBtn',
          type: 'buttons',
          props: {
            buttons: [
              { label: 'Get results', type: 'submit', className: 'btn-2022--secondary' },
            ],
          },
        },
      ],
    },
  ];

  private onNumberOfApplicantsChange(numberOfApplicants: number) {
    if (!this.productsSearchService.model) {
      this.productsSearchService.updateModel();
    }

    const model = this.productsSearchService.model;

    if (!model.mortgageRequirements?.applicants) {
      model.mortgageRequirements.applicants = {} as IApplicants;
    }

    if (!model.mortgageRequirements.applicants.applicantAges) {
      model.mortgageRequirements.applicants.applicantAges
        = this.helperService.generateEmptyApplicantAges(numberOfApplicants);
      this.productsSearchService.updateModel(this.productsSearchService.model);
      return;
    }

    const applicantAges = model.mortgageRequirements.applicants.applicantAges;

    if (applicantAges.length > numberOfApplicants) {
      model.mortgageRequirements.applicants.applicantAges = applicantAges?.slice(0, numberOfApplicants);
    } else if (applicantAges.length < numberOfApplicants) {
      const emptyApplicantAges = this.helperService.generateEmptyApplicantAges(
        numberOfApplicants - applicantAges.length,
        applicantAges.length,
      );
      model.mortgageRequirements.applicants.applicantAges.push(...emptyApplicantAges);
    }

    this.productsSearchService.updateModel(this.productsSearchService.model);
  }

  private getMortgageTypeOptions() {
    return [
      { label: 'Purchase', value: MortgageType.Purchase },
      { label: 'Remortgage', value: MortgageType.Remortgage },
      { label: 'Second Charge', value: MortgageType.SecondCharge },
    ];
  }

  private mortgageLenderFields(): FormlyFieldConfig[] {
    const mortgageLenderHidden = (field) => {
      const mortgageType = field?.model?.mortgageType;
      const purchaserType = field?.model?.purchaserType;
      return !(mortgageType === MortgageType.Remortgage ||
        (mortgageType === MortgageType.Purchase && purchaserType === PurchaserType.HomeMover));
    };

    const initialPeriodHidden = (field) => field?.model?.mortgageType !== MortgageType.Remortgage;

    return [
      {
        key: 'mortgageLender',
        wrappers: [WrapperType.NoWrappers],
        props: {
          suppressErrors: true,
        },
        fieldGroup: [
          {
            key: 'value',
            type: FormFieldType.Select,
            props: {
              label: 'Who is the current mortgage Lender?',
              allowNull: true,
            },
            expressions: {
              hide: field => mortgageLenderHidden(field.parent?.parent),
              'props.options': this.lenderService.getUnarchivedLendersNames(this.helperService.getResLendingType())
                .pipe(map(lenderNames => this.helperService.getMortgageLendersList(lenderNames))),
            },
          },
        ],
      },
      {
        key: 'currentMortgageLender',
        type: FormFieldType.Input,
        expressions: {
          hide: field => {
            if (mortgageLenderHidden(field)) {
              return true;
            }

            const mortgageLenderValue = field.model?.mortgageLender?.value;
            return !mortgageLenderValue || mortgageLenderValue !== MortgageLenders.Other;
          },
        },
      },
      {
        key: 'productTransferInitialDate',
        type: FormFieldType.Date,
        props: {
          label: 'What is the end date for the Initial period?',
        },
        validators: {
          validation: [FormsValidators.dateOrEmpty],
        },
        expressions: {
          hide: field => {
            if (initialPeriodHidden(field)) {
              return true;
            }

            return field.model?.mortgageLender?.value === MortgageLenders.Other;
          },
        },
      },
    ];
  }

  private isHelpToBuyHidden(field: FormlyFieldConfig | undefined): boolean {
    const mortgageType = field?.parent?.model?.mortgageType;
    if (mortgageType !== MortgageType.Remortgage && mortgageType !== MortgageType.Purchase) {
      return true;
    }

    return this.isRightToBuySelected(field);
  }

  private isRightToBuySelected(field: FormlyFieldConfig | undefined): boolean {
    const productType = field?.parent?.model?.productTypeExtended;
    return productType === ProductType.RightToBuy;
  }

  private isHelpToBuySelected(field: FormlyFieldConfig | undefined) {
    const helpToBuy = !!field?.parent?.model?.helpToBuy;
    return !this.isHelpToBuyHidden(field) && helpToBuy;
  }
}
